import { objectEntriesTyped } from '@snap-types/util/object-iterators';
import { QuoteType } from '@ui/features/quote-builder/types/state/quote';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { QuoteLineType } from '../../types/state/quote-line';
import {
  quotePriceIsLocalOnly,
  quotePriceIsPersisted,
} from '../selectors/quote-price';
import { deleteIssue } from './issue-managers';
import { stringifyQuote } from './stringify-quote';

/**
 * Keeps quote prices that exist only in the store.
 *
 * Prices are created optimistically, so the store can hold a price the API has
 * never seen: one still being created, or one whose creation failed and is
 * waiting on the user's Retry/Discard. An API response is authoritative about
 * the prices it knows about, but it cannot speak to those, and dropping them
 * would pull the row out from under the Retry/Discard buttons while leaving
 * their Issue behind with nothing left to act on (#3076).
 */
function carryOverLocalOnlyQuotePrices(
  currentQuoteLines: WritableQuoteBuilderStateType['quoteLines'],
  incomingQuoteLines: QuoteType['quoteLines'],
) {
  objectEntriesTyped(currentQuoteLines).forEach(
    ([quoteLineUuid, currentQuoteLine]) => {
      const incomingQuoteLine = incomingQuoteLines[quoteLineUuid];

      if (!incomingQuoteLine) {
        return;
      }

      currentQuoteLine.quotePriceUuids
        .filter(
          (quotePriceUuid) =>
            !incomingQuoteLine.quotePrices[quotePriceUuid] &&
            quotePriceIsLocalOnly(currentQuoteLine.quotePrices[quotePriceUuid]),
        )
        .forEach((quotePriceUuid) => {
          incomingQuoteLine.quotePrices[quotePriceUuid] =
            currentQuoteLine.quotePrices[quotePriceUuid];

          incomingQuoteLine.quotePriceUuids.push(quotePriceUuid);
        });
    },
  );
}

/**
 * Drops Issues the response has settled.
 *
 * A `rejected` Issue records that an API call for a record failed, so a
 * response that reports the record as `ready` has outlived it: the price a
 * failed delete left behind comes back healthy, which clears its red row but
 * used to leave "Failed to Delete Price" in the drawer with no way to dismiss
 * it. An Issue on a record the response dropped altogether can never be
 * resolved or displayed either, whatever its type.
 *
 * Prices held back by `carryOverLocalOnlyQuotePrices` keep their Issue - they
 * are not `ready`, and Retry/Discard is still the only way out of them.
 */
function dropSettledRecordIssues(state: WritableQuoteBuilderStateType) {
  objectEntriesTyped(state.issues).forEach(([issueId, issue]) => {
    if (issue.area === 'quote') {
      return;
    }

    const quoteLine = state.quoteLines[issue.quoteLineUuid];

    if (!quoteLine) {
      deleteIssue(state, issueId);
      return;
    }

    if (issue.area === 'quotePrice') {
      const quotePrice = quoteLine.quotePrices[issue.quotePriceUuid];

      if (
        !quotePrice ||
        (issue.type === 'rejected' && quotePriceIsPersisted(quotePrice))
      ) {
        deleteIssue(state, issueId);
      }

      return;
    }

    if (
      issue.area === 'quoteLine' &&
      issue.type === 'rejected' &&
      quoteLine.status === 'ready'
    ) {
      deleteIssue(state, issueId);
    }
  });
}

export function updateQuoteData(
  state: WritableQuoteBuilderStateType,
  quote: QuoteType,
) {
  const currentUiState = {
    // Preserve certain UI-state values that shouldn't be overwritten
    // by the API response
    dialog: { ...state.dialog },
    partSearchColumns: [...state.partSearchColumns],
    isIssueDrawerOpen: state.isIssueDrawerOpen,
    lastSaveDateTime: state.lastSaveDateTime,
    quoteLineUiState: objectEntriesTyped(state.quoteLines).map(
      ([uuid, data]) => ({
        uuid,
        infoTab: data.infoTab,
        isInfoVisible: data.isInfoVisible,
        infoTabOrderHistoryFilterByCustomer:
          data.infoTabOrderHistoryFilterByCustomer,
        infoTabQuoteHistoryFilterByCustomer:
          data.infoTabQuoteHistoryFilterByCustomer,
        infoTabOrderHistoryExactMatch: data.infoTabOrderHistoryExactMatch,
        infoTabQuoteHistoryExactMatch: data.infoTabQuoteHistoryExactMatch,
      }),
    ),
  };

  carryOverLocalOnlyQuotePrices(state.quoteLines, quote.quoteLines);

  Object.assign(state, quote, currentUiState);

  // Update quote line UI state for each quote line that remained in the payload
  state.quoteLines = objectEntriesTyped(state.quoteLines).reduce(
    (acc, [uuid, data]) => {
      acc[uuid] = {
        ...data,
        ...(currentUiState.quoteLineUiState.find(
          (item) => item.uuid === uuid,
        ) || {}),
      };
      return acc;
    },
    {} as Record<string, QuoteLineType>,
  );

  dropSettledRecordIssues(state);

  state.isLoading = false;
  state.isUpdating = false;

  // If the currently selected Market/Discount Code per the API response isn't on the limited list
  // of Site Codes, then we need to show ALL the Market/Discount Codes
  state.showAllDiscountCodes = Boolean(
    quote.discountCode &&
      quote.siteDiscountCodes?.length &&
      !quote.siteDiscountCodes.includes(quote.discountCode),
  );

  state.showAllMarketCodes = Boolean(
    quote.marketCode &&
      quote.siteMarketCodes?.length &&
      !quote.siteMarketCodes.includes(quote.marketCode),
  );

  state.currentServerData = stringifyQuote(state);
}
